import React, { Component } from "react";
import { Button, TextField } from "@material-ui/core";
import CircularProgress from "@material-ui/core/CircularProgress";
import logo from "assets/img/front-logo.png";
import "assets/css/login.css";
import { twofaService } from "_services/twofa.service";
import { OTP_COOLDOWN_SECONDS, OTP_LENGTH } from "__helpers/constants";

class OTPVerification extends Component {
  constructor(props) {
    super(props);
    this.state = {
      otpDigits: Array(OTP_LENGTH).fill(""),
      loading: false,
      resending: false,
      error: "",
      cooldown: OTP_COOLDOWN_SECONDS,
    };
    this.inputRefs = Array(OTP_LENGTH)
      .fill(null)
      .map(() => React.createRef());
    this.cooldownTimer = null;
  }

  componentDidMount() {
    this.startCooldown();
    this.focusFirstInput();
  }

  focusFirstInput = () => {
    const focusOtp = () => {
      if (this.inputRefs[0] && this.inputRefs[0].current) {
        this.inputRefs[0].current.focus();
      }
    };

    // Remove the iOS keyboard bridge input (created in Login.jsx handleSubmit).
    // Transferring focus from it to the real OTP input keeps the keyboard open.
    const bridgeInput = document.getElementById("ios-keyboard-bridge");
    if (bridgeInput) {
      // Small delay to let React render the input, then transfer focus
      requestAnimationFrame(() => {
        focusOtp();
        // Clean up the bridge element after focus transfer
        setTimeout(() => {
          const el = document.getElementById("ios-keyboard-bridge");
          if (el && el.parentNode) {
            el.parentNode.removeChild(el);
          }
        }, 500);
      });
    } else {
      // Non-iOS: standard focus strategies
      focusOtp();
      requestAnimationFrame(() => {
        focusOtp();
      });
      setTimeout(() => {
        focusOtp();
      }, 300);
    }
  };

  componentWillUnmount() {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
    }
  }

  startCooldown = () => {
    this.setState({ cooldown: OTP_COOLDOWN_SECONDS });
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
    }
    this.cooldownTimer = setInterval(() => {
      this.setState((prev) => {
        if (prev.cooldown <= 1) {
          clearInterval(this.cooldownTimer);
          return { cooldown: 0 };
        }
        return { cooldown: prev.cooldown - 1 };
      });
    }, 1000);
  };

  handleDigitChange = (index, value) => {
    // Handle OS autofill (iOS/Android) — full OTP arrives as a multi-char value
    const digits = value.replace(/[^0-9]/g, "");
    if (digits.length > 1) {
      const otpDigits = [...this.state.otpDigits];
      for (let i = 0; i < Math.min(digits.length, OTP_LENGTH); i++) {
        otpDigits[i] = digits[i];
      }
      this.setState({ otpDigits, error: "" }, () => {
        if (otpDigits.every(d => d !== "")) {
          this.handleVerify();
        } else {
          const focusIndex = Math.min(digits.length, OTP_LENGTH) - 1;
          if (this.inputRefs[focusIndex] && this.inputRefs[focusIndex].current) {
            this.inputRefs[focusIndex].current.focus();
          }
        }
      });
      return;
    }

    if (digits.length === 1) {
      value = digits;
    } else if (value !== "") {
      return;
    } else {
      value = "";
    }

    const otpDigits = [...this.state.otpDigits];
    otpDigits[index] = value;
    this.setState({ otpDigits, error: "" }, () => {
      // Auto-submit when all digits are filled
      if (value && otpDigits.every(d => d !== "")) {
        this.handleVerify();
        return;
      }
      // Auto-advance to next field
      if (value && index < OTP_LENGTH - 1) {
        this.inputRefs[index + 1].current.focus();
      }
    });
  };

  handleKeyDown = (index, e) => {
    // Backspace: clear current and move to previous
    if (e.key === "Backspace" && !this.state.otpDigits[index] && index > 0) {
      this.inputRefs[index - 1].current.focus();
    }
  };

  handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/[^0-9]/g, "");
    if (pasted.length > 0) {
      const otpDigits = [...this.state.otpDigits];
      for (let i = 0; i < Math.min(pasted.length, OTP_LENGTH); i++) {
        otpDigits[i] = pasted[i];
      }
      this.setState({ otpDigits, error: "" }, () => {
        if (otpDigits.every(d => d !== "")) {
          this.handleVerify();
        } else {
          const focusIndex = Math.min(pasted.length, OTP_LENGTH) - 1;
          if (this.inputRefs[focusIndex] && this.inputRefs[focusIndex].current) {
            this.inputRefs[focusIndex].current.focus();
          }
        }
      });
    }
  };

  getOtpCode = () => {
    return this.state.otpDigits.join("");
  };

  handleVerify = async () => {
    const otpCode = this.getOtpCode();
    if (otpCode.length !== OTP_LENGTH) {
      this.setState({ error: "Please enter the complete verification code" });
      return;
    }

    this.setState({ loading: true, error: "" });

    try {
      const data = await twofaService.verifyOtp(this.props.preAuthToken, otpCode);

      if (data.status === "INVALID_OTP") {
        this.setState({
          error: data.message || "Invalid code. Please try again.",
          otpDigits: Array(OTP_LENGTH).fill(""),
        });
        if (this.inputRefs[0] && this.inputRefs[0].current) {
          this.inputRefs[0].current.focus();
        }
      } else if (data.status === "SESSION_EXPIRED") {
        this.props.onSessionExpired();
      } else if (data.access_token) {
        // Success — pass the token data back to Login
        this.props.onVerified(data);
      } else {
        this.setState({
          error: data.message || "Verification failed. Please try again.",
        });
      }
    } catch (err) {
      this.setState({
        error: err.message || "Unable to verify. Please try again.",
      });
    }

    this.setState({ loading: false });
  };

  handleResend = async () => {
    this.setState({ resending: true, error: "" });

    try {
      const data = await twofaService.sendOtp(this.props.preAuthToken);

      if (data.status === "OTP_SENT") {
        this.setState({ otpDigits: Array(OTP_LENGTH).fill("") });
        this.startCooldown();
        if (this.inputRefs[0] && this.inputRefs[0].current) {
          this.inputRefs[0].current.focus();
        }
      } else if (data.status === "SESSION_EXPIRED") {
        this.props.onSessionExpired();
      } else if (data.status === "RATE_LIMITED") {
        this.setState({ error: data.message || "Too many attempts. Please try again later." });
      } else {
        this.setState({ error: data.message || "Failed to resend code" });
      }
    } catch (err) {
      this.setState({
        error: err.message || "Unable to resend code. Please try again.",
      });
    }

    this.setState({ resending: false });
  };

  render() {
    const { cooldown } = this.state;

    return (
      <div className="login-outer-cover">
        <div className="login-cover gray-bg-color">
          <div className="logo-content loginLogoMain" ref={(el) => { this.logoRef = el; }}>
            <div className="loginLogoInner">
              <img src={logo} alt="logo" />
            </div>
          </div>
          <div className="login-inner-cover">
            <div className="login-content">
              <div className="Login">
                <h3 style={{ marginBottom: "10px", textAlign: "center" }}>
                  Enter Verification Code
                </h3>
                <p style={{ marginBottom: "20px", textAlign: "center", color: "#666" }}>
                  We sent a code to {this.props.maskedPhone}
                </p>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "8px",
                    marginBottom: "20px",
                  }}
                >
                  {this.state.otpDigits.map((digit, index) => (
                    <TextField
                      key={index}
                      inputRef={this.inputRefs[index]}
                      autoFocus={index === 0}
                      value={digit}
                      onChange={(e) => this.handleDigitChange(index, e.target.value)}
                      onKeyDown={(e) => this.handleKeyDown(index, e)}
                      onPaste={index === 0 ? this.handlePaste : undefined}
                      onFocus={() => {
                        setTimeout(() => {
                          if (this.logoRef) {
                            this.logoRef.scrollIntoView({ block: "start", behavior: "smooth" });
                          }
                        }, 300);
                      }}
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        autoComplete: index === 0 ? "one-time-code" : "off",
                        style: {
                          textAlign: "center",
                          fontSize: "24px",
                          width: "40px",
                          padding: "10px 0",
                        },
                      }}
                      variant="outlined"
                      style={index === 0 && this.state.otpDigits[0] === "" ? {
                        borderRadius: "4px",
                        boxShadow: "0 0 0 2px #1976d2",
                      } : {}}
                    />
                  ))}
                </div>

                {this.state.error && (
                  <p style={{ color: "red", fontSize: "13px", marginBottom: "10px", textAlign: "center" }}>
                    {this.state.error}
                  </p>
                )}

                <div className="action-btns" style={{ marginBottom: "10px" }}>
                  <Button
                    color="primary"
                    className={this.state.loading ? "buttonSuccess btn1" : "btn1"}
                    disabled={this.state.loading || this.getOtpCode().length !== OTP_LENGTH}
                    onClick={this.handleVerify}
                  >
                    Verify
                    {this.state.loading && (
                      <CircularProgress size={24} className="buttonProgress" />
                    )}
                  </Button>
                </div>

                <div style={{ textAlign: "center", marginBottom: "10px" }}>
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (cooldown === 0 && !this.state.resending) {
                        this.handleResend();
                      }
                    }}
                    style={{
                      color: cooldown > 0 || this.state.resending ? "#999" : "#1976d2",
                      fontSize: "14px",
                      cursor: cooldown > 0 || this.state.resending ? "default" : "pointer",
                    }}
                  >
                    {this.state.resending
                      ? "Sending..."
                      : cooldown > 0
                      ? `Resend code in ${cooldown}s`
                      : "Resend code"}
                  </a>
                </div>

                {/* <div style={{ textAlign: "center" }}>
                  <Button
                    color="default"
                    style={{ textTransform: "none", color: "#1976d2" }}
                    onClick={this.props.onChangePhone}
                  >
                    Use a different phone number
                  </Button>
                </div> */}

                <div style={{ textAlign: "center", marginTop: "10px" }}>
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); this.props.onBackToLogin(); }}
                    style={{ color: "#1976d2", fontSize: "14px" }}
                  >
                    Back to Login
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default OTPVerification;
